class BundleUpsellPopup extends HTMLElement {
  constructor() {
    super();
    this.activeData = null;
    this.state = { sellingPlan: null };
    this.upgradeToken = 0;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.grid = this.querySelector('.bundle-upsell-popup__grid');
    this.titleEl = this.querySelector('[data-role="bundle-title"]');
    this.heroContainers = this.querySelectorAll('.bundle-upsell-popup__hero');
    this.heroImgs = this.querySelectorAll('[data-role="hero"]');
    this.priceEl = this.querySelector('[data-role="bundle-price"]');
    this.compareEl = this.querySelector('[data-role="compare-price"]');
    this.saveEl = this.querySelector('[data-role="save-amount"]');
    this.itemsEl = this.querySelector('[data-role="items"]');
    this.itemTemplate = this.querySelector('[data-role="item-template"]');
    this.ctaEl = this.querySelector('[data-action="upgrade"]');
    this.dismissEl = this.querySelector('.bundle-upsell-popup__dismiss');

    this.dataMap = new Map();
    this.variantToProductId = new Map();
    this.skipProducts = new Set();
    this.inflight = new Map();
    this.bundleVariantIds = new Set();
    this.justDismissedProductIds = new Set();
    this.ingestedScripts = new WeakSet();

    this.triggerQuantity = 1;
    this.popupProductId = null;
    this.pendingAddQtyByVariant = new Map();

    this.lastPointerTarget = null;
    this.previouslyFocused = null;

    this.cartDrawer = document.querySelector('cart-drawer');

    this.onAdded = this.onAdded.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onUpgrade = this.onUpgrade.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onSubmitSnapshotQty = this.onSubmitSnapshotQty.bind(this);
    this.onPointerDown = (e) => { this.lastPointerTarget = e.target; };

    this.ingestAllScripts();

    document.addEventListener('submit', this.onSubmitSnapshotQty, { capture: true });
    document.addEventListener('product-ajax:added', this.onAdded);
    document.addEventListener('pointerdown', this.onPointerDown, { capture: true, passive: true });
    this.addEventListener('keydown', this.onKeydown);

    this.querySelectorAll('[data-close]').forEach((el) =>
      el.addEventListener('click', this.onClose)
    );
    this.ctaEl.addEventListener('click', this.onUpgrade);
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this.onSubmitSnapshotQty, { capture: true });
    document.removeEventListener('product-ajax:added', this.onAdded);
    document.removeEventListener('pointerdown', this.onPointerDown, { capture: true });
    this.removeEventListener('keydown', this.onKeydown);
    this._initialized = false;
  }

  onSubmitSnapshotQty(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('form[action*="/cart/add"]')) return;
    const idInput = form.querySelector('[name="id"]');
    if (!idInput || !idInput.value) return;
    const variantId = Number(idInput.value);
    if (!Number.isFinite(variantId)) return;
    const qtyInput = form.querySelector('[name="quantity"]');
    const qtyValue = qtyInput ? Number(qtyInput.value) : 1;
    const qty = Number.isFinite(qtyValue) && qtyValue > 0 ? qtyValue : 1;
    this.pendingAddQtyByVariant.set(variantId, qty);
  }

  ingestAllScripts() {
    document.querySelectorAll('script.bundle-upsell-data').forEach((el) => this.ingestScript(el));
  }

  cacheData(productId, data) {
    this.dataMap.set(productId, data);
    if (data.bundleVariantId) this.bundleVariantIds.add(Number(data.bundleVariantId));
    if (Array.isArray(data.triggerVariantIds)) {
      data.triggerVariantIds.forEach((vid) => {
        const n = Number(vid);
        if (Number.isFinite(n)) this.variantToProductId.set(n, productId);
      });
    }
  }

  ingestScript(el) {
    if (this.ingestedScripts.has(el)) return;
    this.ingestedScripts.add(el);
    try {
      const data = JSON.parse(el.textContent);
      const productId = Number(data.triggerProductId) || Number(el.dataset.triggerProductId);
      if (!productId) return;
      this.cacheData(productId, data);
    } catch (err) {
      console.error('[bundle-upsell-popup] invalid data script', err);
    }
  }

  async fetchData(handle, productId) {
    if (this.inflight.has(productId)) return this.inflight.get(productId);
    const promise = (async () => {
      try {
        const res = await fetch(`/products/${encodeURIComponent(handle)}?section_id=bundle-upsell-data`, {
          headers: { Accept: 'text/html' },
        });
        if (!res.ok) return null;
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const script = doc.querySelector('script.bundle-upsell-data');
        if (!script) return null;
        const data = JSON.parse(script.textContent);
        this.cacheData(productId, data);
        return data;
      } catch (err) {
        console.error('[bundle-upsell-popup] fetch failed', productId, err);
        return null;
      } finally {
        this.inflight.delete(productId);
      }
    })();
    this.inflight.set(productId, promise);
    return promise;
  }

  async onAdded(event) {
    if (this.dataset.abDisabled === 'true') return;

    const raw = event.detail && event.detail.product;
    if (!raw || !this.hidden) return;

    const norm = this.normalizeAddPayload(raw);
    if (!norm) return;

    if (norm.variant_id && this.bundleVariantIds.has(norm.variant_id)) return;

    const productId = norm.product_id;
    if (!productId) return;
    if (this.skipProducts.has(productId)) return;

    let data = this.dataMap.get(productId);
    if (!data) {
      if (!norm.handle) {
        this.skipProducts.add(productId);
        return;
      }
      data = await this.fetchData(norm.handle, productId);
      if (!data) {
        this.skipProducts.add(productId);
        return;
      }
    }

    const threshold = Number(this.dataset.smartBundlingThreshold) || 0;
    let cartSnapshot = null;
    if (threshold > 0) {
      cartSnapshot = await this.fetchCart();
      if (cartSnapshot) {
        const memberProductIds = new Set((data.members || []).map((m) => Number(m.id)));
        const uniqueInCart = new Set(
          (cartSnapshot.items || [])
            .map((line) => Number(line.product_id))
            .filter((pid) => memberProductIds.has(pid))
        );
        if (uniqueInCart.size > threshold) return;
      }
    }

    this.popupProductId = productId;
    this.activeData = data;
    this.triggerVariantId = norm.variant_id || null;
    this.triggerPlanId = norm.selling_plan_id || this.readSkioPlanForVariant(norm.variant_id);
    const addedQty = norm.variant_id ? this.pendingAddQtyByVariant.get(norm.variant_id) : null;
    if (norm.variant_id) this.pendingAddQtyByVariant.delete(norm.variant_id);
    this.triggerQuantity = Number.isFinite(addedQty) && addedQty > 0 ? addedQty : 1;

    const bundlePlan = this.resolveBundlePlan(data, this.triggerPlanId);
    this.addedSellingPlanId = bundlePlan ? Number(bundlePlan.id) : null;
    this.state.sellingPlan = bundlePlan || null;

    this.populate(data, productId);
    this.open(cartSnapshot);
  }

  readActivePlanId(form) {
    if (!form) return '';

    const productInfo = form.closest('product-info');
    const scope = productInfo || document;

    if (form.id) {
      const compact =
        scope.querySelector(`compact-skio-plan-picker[form-id="${CSS.escape(form.id)}"]`) ||
        (productInfo ? document.querySelector(`compact-skio-plan-picker[form-id="${CSS.escape(form.id)}"]`) : null);
      if (compact && compact.select != null) {
        return compact.select.value || '';
      }
    }

    const skioPickers = scope.querySelectorAll('skio-plan-picker');
    for (const picker of skioPickers) {
      if (picker.form === form || (form.id && picker.form?.id === form.id)) {
        return picker.selectedSellingPlan
          ? String(picker.selectedSellingPlan.id)
          : '';
      }
    }
    if (!productInfo) {
      const allPickers = document.querySelectorAll('skio-plan-picker');
      if (allPickers.length === 1) {
        const p = allPickers[0];
        return p.selectedSellingPlan ? String(p.selectedSellingPlan.id) : '';
      }
    }

    const inputs = [
      ...(form.id
        ? document.querySelectorAll(`[name="selling_plan"][form="${CSS.escape(form.id)}"]`)
        : []),
      ...form.querySelectorAll('[name="selling_plan"]'),
    ];
    for (const input of inputs) {
      if (input.value) return input.value;
    }
    return '';
  }

  readSkioPlanForVariant(variantId) {
    const targetVariantId = Number(variantId);
    if (!Number.isFinite(targetVariantId) || targetVariantId <= 0) return null;

    const readVariantIdFromForm = (form) => {
      if (!form) return null;
      const idEls = form.querySelectorAll('[name="id"]');
      for (const el of idEls) {
        if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) {
          const n = Number(el.value);
          if (Number.isFinite(n) && n > 0) return n;
        }
      }
      return null;
    };

    const readPlanFromForm = (form) => {
      const v = this.readActivePlanId(form);
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : null;
    };

    if (this.lastPointerTarget instanceof Element && this.lastPointerTarget.isConnected) {
      const ownerForm = this.lastPointerTarget.closest('form[action*="/cart/add"]');
      if (ownerForm && readVariantIdFromForm(ownerForm) === targetVariantId) {
        return readPlanFromForm(ownerForm);
      }
    }

    const scope =
      (this.lastPointerTarget instanceof Element && this.lastPointerTarget.closest('product-info')) ||
      document;
    const forms = scope.querySelectorAll('form[action*="/cart/add"]');
    let matched = null;
    let multiple = false;
    for (const form of forms) {
      if (readVariantIdFromForm(form) !== targetVariantId) continue;
      if (matched) { multiple = true; break; }
      matched = form;
    }
    if (matched && !multiple) return readPlanFromForm(matched);

    if (scope !== document) {
      const docForms = document.querySelectorAll('form[action*="/cart/add"]');
      matched = null;
      multiple = false;
      for (const form of docForms) {
        if (readVariantIdFromForm(form) !== targetVariantId) continue;
        if (matched) { multiple = true; break; }
        matched = form;
      }
      if (matched && !multiple) return readPlanFromForm(matched);
    }

    return null;
  }

  normalizeAddPayload(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const item = Array.isArray(raw.items) && raw.items[0] ? raw.items[0] : raw;
    if (!item || typeof item !== 'object') return null;

    const variantId = Number(item.variant_id ?? item.id);
    const productId = Number(item.product_id);
    const quantity = Number(item.quantity);

    let planId = null;
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      planId = Number(item.selling_plan_allocation.selling_plan.id);
    } else if (item.selling_plan != null && item.selling_plan !== '') {
      planId = Number(item.selling_plan);
    }

    return {
      variant_id: Number.isFinite(variantId) ? variantId : null,
      product_id: Number.isFinite(productId) ? productId : null,
      handle: item.handle || item.product_handle || null,
      selling_plan_id: Number.isFinite(planId) ? planId : null,
      quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
    };
  }

  findCartLine(cart, variantId, planId) {
    if (!cart || !Array.isArray(cart.items) || !variantId) return null;
    const linePlanId = (line) =>
      line.selling_plan_allocation && line.selling_plan_allocation.selling_plan
        ? Number(line.selling_plan_allocation.selling_plan.id)
        : null;

    const exact = cart.items.find(
      (line) => Number(line.variant_id) === variantId && linePlanId(line) === planId
    );
    if (exact) return exact;

    const byVariant = cart.items.find((line) => Number(line.variant_id) === variantId);
    if (byVariant) {
      console.warn(
        '[bundle-upsell-popup] trigger line plan mismatch; decrementing by variant only',
        { expectedPlanId: planId, foundPlanId: linePlanId(byVariant) }
      );
      return byVariant;
    }
    return null;
  }

  resolveBundlePlan(data, triggerPlanId) {
    const bundlePlans = Array.isArray(data.bundlePlans) ? data.bundlePlans : [];
    if (!triggerPlanId || bundlePlans.length === 0) return null;

    const triggerPlans = Array.isArray(data.triggerPlans) ? data.triggerPlans : [];
    const triggerPlan = triggerPlans.find((p) => Number(p.id) === Number(triggerPlanId));
    if (!triggerPlan) return null;

    const norm = (s) => (s == null ? '' : String(s).trim().toLowerCase());
    const targetName = norm(triggerPlan.name);
    const byName = bundlePlans.find((p) => norm(p.name) === targetName);
    if (byName) return byName;

    console.warn(
      '[bundle-upsell-popup] no bundle plan matches trigger plan',
      {
        triggerPlanName: triggerPlan.name,
        bundlePlanNames: bundlePlans.map((p) => p.name),
      }
    );
    return null;
  }

  populate(data, triggerProductId) {
    if (this.titleEl) this.titleEl.textContent = data.bundleTitle || '';
    const hasHero = Boolean(data.heroImageUrl);
    this.heroContainers.forEach((el) => {
      el.hidden = !hasHero;
    });
    if (this.grid) this.grid.classList.toggle('no-hero', !hasHero);
    this.heroImgs.forEach((img) => {
      if (hasHero) {
        img.src = data.heroImageUrl;
        img.alt = data.heroImageAlt || data.bundleTitle || '';
      } else {
        img.removeAttribute('src');
      }
    });

    if (this.itemsEl && this.itemTemplate) {
      this.itemsEl.innerHTML = '';
      const isTriggerMember = (m) =>
        m.is_trigger === true || Number(m.id) === Number(triggerProductId);
      const members = (data.members || []).slice().sort((a, b) => {
        const aT = isTriggerMember(a);
        const bT = isTriggerMember(b);
        return aT === bT ? 0 : aT ? -1 : 1;
      });
      members.forEach((member) => {
        const frag = this.itemTemplate.content.cloneNode(true);
        const li = frag.querySelector('li');
        const label = frag.querySelector('[data-role="item-label"]');
        const img = frag.querySelector('[data-role="item-image"]');
        const rating = frag.querySelector('[data-role="item-rating"]');
        const title = frag.querySelector('[data-role="item-title"]');
        const features = frag.querySelector('[data-role="item-features"]');

        const isTrigger = isTriggerMember(member);
        li.dataset.productId = member.id || '';

        if (isTrigger) {
          li.classList.add('is-in-cart');
          if (label) label.hidden = false;
        }
        if (img) {
          if (member.image_url) {
            img.src = member.image_url;
            img.alt = member.image_alt || member.title || '';
          } else {
            img.remove();
          }
        }
        if (rating) {
          if (member.rating_html) {
            rating.innerHTML = member.rating_html;
            rating.hidden = false;
          } else {
            rating.remove();
          }
        }
        if (title) title.textContent = member.title || '';
        if (features) {
          if (member.features_text) {
            features.textContent = member.features_text;
            features.hidden = false;
          } else {
            features.remove();
          }
        }
        this.itemsEl.appendChild(frag);
      });
    }
  }

  open(cartSnapshot) {
    this.previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.hidden = false;
    document.body.classList.add('bundle-upsell-popup-open');
    this.updatePrice();
    if (this.ctaEl && !this.ctaEl.disabled) {
      this.ctaEl.focus();
    } else {
      const focusables = this.getFocusables();
      if (focusables.length) focusables[0].focus();
    }
    this.refreshInCartBadges(cartSnapshot);
  }

  async fetchCart() {
    const routes = window.FoxTheme && FoxTheme.routes;
    if (!routes) return null;
    try {
      const res = await fetch(routes.cart_url, {
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (err) {
      console.error('[bundle-upsell-popup] cart fetch failed', err);
      return null;
    }
  }

  async refreshInCartBadges(cartSnapshot) {
    let cart = cartSnapshot || await this.fetchCart();
    if (!cart || this.hidden) return;
    const cartProductIds = new Set((cart.items || []).map((line) => Number(line.product_id)));
    this.querySelectorAll('.bundle-upsell-popup__item[data-product-id]').forEach((li) => {
      const pid = Number(li.dataset.productId);
      if (!pid || !cartProductIds.has(pid)) return;
      li.classList.add('is-in-cart');
      const labelEl = li.querySelector('[data-role="item-label"]');
      if (labelEl) labelEl.hidden = false;
    });
  }

  onKeydown(event) {
    if (this.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.onClose(event);
      return;
    }
    if (event.key === 'Tab') {
      const focusables = this.getFocusables();
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  getFocusables() {
    return Array.from(
      this.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hidden && !el.disabled && el.offsetParent !== null);
  }

  onClose(event) {
    const dismissedByUser = Boolean(event);
    const isDismiss = event && event.currentTarget === this.dismissEl;
    const goToCheckout = isDismiss && this.dataset.dismissToCheckout === 'true';

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (dismissedByUser) this.upgradeToken++;
    this.hidden = true;
    document.body.classList.remove('bundle-upsell-popup-open');
    if (this.ctaEl) {
      this.ctaEl.disabled = false;
      this.ctaEl.classList.remove('btn--loading');
    }

    const toRestore = this.previouslyFocused;
    this.previouslyFocused = null;
    if (toRestore && toRestore.isConnected) {
      try { toRestore.focus(); } catch (_) { /* no-op */ }
    }

    if (!dismissedByUser) return;

    this.popupProductId = null;

    if (goToCheckout) {
      window.location.href = '/checkout';
    }
  }

  // Returns the Intelligems test-aware price (in cents) for a variant, falling
  // back to the server-rendered base price when Intelligems has no price for it
  // (test inactive, plugin not ready, etc.). getPriceByVariantId returns dollars
  // as a string (e.g. "47.99"); the popup works in cents.
  igTestPriceCents(variantId, fallbackCents) {
    const api = window.igData && window.igData.price;
    if (api && variantId && typeof api.getPriceByVariantId === 'function') {
      try {
        const dollars = api.getPriceByVariantId(variantId);
        if (dollars != null && dollars !== '') {
          const cents = Math.round(parseFloat(dollars) * 100);
          if (Number.isFinite(cents) && cents > 0) return cents;
        }
      } catch (_) { /* fall back below */ }
    }
    return fallbackCents;
  }

  // "Buy separately" total = sum of each member's Intelligems test price so the
  // strikethrough reflects any active price test on the member products. Falls
  // back to the server-computed childrenTotal if members lack variant data.
  childrenTotalCents() {
    const members = Array.isArray(this.activeData.members) ? this.activeData.members : [];
    const withVariants = members.filter((m) => m.variant_id);
    if (withVariants.length === 0) return this.activeData.childrenTotal;
    return withVariants.reduce(
      (sum, m) => sum + this.igTestPriceCents(m.variant_id, Number(m.price) || 0),
      0
    );
  }

  updatePrice() {
    if (!this.activeData || !this.priceEl || !this.compareEl) return;
    const effective = this.effectiveBundlePrice();
    // Strikethrough = sum of the member products' Intelligems test prices.
    // Savings = that total minus the bundle's sale price (its Intelligems test
    // price when the bundle is in a test, otherwise its displayed price).
    const compare = this.childrenTotalCents();
    const bundleSale = this.igTestPriceCents(this.activeData.bundleVariantId, effective);
    const save = Math.max(0, compare - bundleSale);

    this.priceEl.textContent = window.FoxTheme.Currency.formatMoney(effective);
    this.compareEl.textContent = window.FoxTheme.Currency.formatMoney(compare);
    this.compareEl.hidden = save === 0;

    if (this.saveEl) {
      this.saveEl.textContent = window.FoxTheme.Currency.formatMoney(save);
    }

    // Intelligems: simply tag the bundle price (no recalculation) so that if the
    // bundle product is included in a test, Intelligems shows its test price here.
    // The compare and savings are derived sums, so they are computed above from the
    // members' test prices rather than tagged.
    if (this.activeData.bundleVariantId) {
      this.priceEl.classList.add('igPrice');
      this.priceEl.setAttribute('data-variant-id', this.activeData.bundleVariantId);
      if (this.activeData.bundleProductId) {
        this.priceEl.setAttribute('data-product-id', this.activeData.bundleProductId);
      }
    }
  }

  effectiveBundlePrice() {
    const base = this.activeData.bundleBasePrice;
    const plan = this.state.sellingPlan;
    if (!plan) return base;

    if (plan.perDeliveryPrice != null) {
      const n = Number(plan.perDeliveryPrice);
      if (Number.isFinite(n)) return n;
    }

    if (plan.adjustmentType && plan.adjustmentValue != null) {
      const value = Number(plan.adjustmentValue);
      if (Number.isFinite(value)) {
        switch (plan.adjustmentType) {
          case 'percentage':
            return Math.round(base * (1 - value / 100));
          case 'fixed_amount':
            return Math.max(0, base - value);
          case 'price':
            return value;
        }
      }
    }

    return base;
  }

  async onUpgrade(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.ctaEl.disabled || !this.activeData) return;
    this.ctaEl.disabled = true;
    this.ctaEl.classList.add('btn--loading');

    const token = ++this.upgradeToken;
    const isStale = () => token !== this.upgradeToken;

    const routes = window.FoxTheme && FoxTheme.routes;
    if (!routes) {
      this.onClose();
      return;
    }

    const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

    const qty = Math.max(1, Number(this.triggerQuantity) || 1);
    const triggerVariantSnap = this.triggerVariantId;
    const triggerPlanSnap = this.triggerPlanId;
    const triggerQuantitySnap = this.triggerQuantity;

    let addResult = null;
    try {
      const addItem = { id: this.activeData.bundleVariantId, quantity: qty };
      if (this.addedSellingPlanId) addItem.selling_plan = this.addedSellingPlanId;

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );

      const addResponse = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({
          items: [addItem],
          sections: sectionsToBundle,
          sections_url: window.location.pathname,
        }),
      });
      addResult = await addResponse.json();
      if (addResult.status || addResult.errors) {
        throw new Error(addResult.description || addResult.message || 'add failed');
      }
    } catch (err) {
      console.error('[bundle-upsell-popup] upgrade failed', err);
      if (!isStale()) {
        this.ctaEl.disabled = false;
        this.ctaEl.classList.remove('btn--loading');
      }
      return;
    }

    let postChangeCart = null;
    const decrement = Math.max(1, Number(triggerQuantitySnap) || 1);
    try {
      const cart = await fetch(routes.cart_url, { headers: jsonHeaders }).then((r) => r.json());
      const updates = {};

      if (triggerVariantSnap) {
        const triggerLine = this.findCartLine(cart, triggerVariantSnap, triggerPlanSnap);
        if (triggerLine && triggerLine.key) {
          updates[triggerLine.key] = Math.max(0, Number(triggerLine.quantity) - decrement);
        }
      }

      const coMemberIds = new Set(
        (this.activeData.members || [])
          .map((m) => Number(m.id))
          .filter((id) => id !== Number(this.popupProductId))
      );
      for (const line of cart.items || []) {
        if (coMemberIds.has(Number(line.product_id)) && line.key) {
          updates[line.key] = Math.max(0, Number(line.quantity) - decrement);
        }
      }

      if (Object.keys(updates).length > 0) {
        const changeRes = await fetch(routes.cart_update_url, {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({ updates }),
        });
        postChangeCart = await changeRes.json();
      }
    } catch (err) {
      console.error('[bundle-upsell-popup] cart decrement failed', err);
    }

    if (!isStale()) this.onClose();

    const hasSections = addResult.sections && Object.keys(addResult.sections).length > 0;
    const pubsub = window.FoxTheme && FoxTheme.pubsub;

    try {
      if (pubsub && pubsub.PUB_SUB_EVENTS) {
        const cart =
          postChangeCart ||
          (await fetch(routes.cart_url, { headers: jsonHeaders }).then((r) => r.json()));
        if (hasSections) cart.sections = addResult.sections;
        pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
      }

      if (this.cartDrawer || document.querySelector('cart-drawer')) {
        document.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } })
        );
      } else if (!isStale()) {
        window.location.href = routes.cart_url;
      }

      const addedLine = addResult.items ? addResult.items[0] : addResult;
      if (addedLine && Number.isFinite(Number(addedLine.variant_id))) {
        document.dispatchEvent(
          new CustomEvent('product-ajax:added', {
            bubbles: true,
            detail: { product: addedLine },
          })
        );
      }
    } catch (err) {
      console.error('[bundle-upsell-popup] cart refresh failed', err);
    }
  }
}

if (!customElements.get('bundle-upsell-popup')) {
  customElements.define('bundle-upsell-popup', BundleUpsellPopup);
}
